/**
 * Authentication Store
 *
 * Manages user authentication state with persistence
 * Uses Zustand with localStorage persistence
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { setAuthSession, clearAuthSession, isAuthenticated } from '../utils/auth';
import { setAmplitudeUser } from '../utils/amplitude';

/**
 * Auth store with automatic persistence
 */
export const useAuthStore = create(
  persist(
    (set, get) => ({
      // State
      user: null,
      isAuthenticated: false,
      isLoading: false,

      // Actions
      /**
       * Set authenticated user
       * @param {object} userData - User data from API
       * @param {string} token - JWT token
       */
      login: (userData, token) => {
        // Store in localStorage via auth utils (for backward compatibility)
        setAuthSession(token, userData.id);
        setAmplitudeUser(userData.id);

        set({
          user: userData,
          isAuthenticated: true,
        });
      },

      /**
       * Clear user session
       */
      logout: () => {
        clearAuthSession();
        set({
          user: null,
          isAuthenticated: false,
        });
      },

      /**
       * Update user profile data
       * @param {object} updates - Partial user data to update
       */
      updateUser: (updates) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        }));
      },

      /**
       * Check if user is authenticated (useful for components)
       */
      checkAuth: () => {
        const authenticated = isAuthenticated();

        set({
          isAuthenticated: authenticated,
        });

        if (authenticated) {
          const { user } = get();
          if (user?.id) setAmplitudeUser(user.id);
        }

        return authenticated;
      },

      /**
       * Set loading state
       */
      setLoading: (isLoading) => set({ isLoading }),
    }),
    {
      name: 'auth-storage', // localStorage key
      partialize: (state) => ({
        // Only persist these fields
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Export selectors for better performance (components only re-render when their data changes)
export const selectUser = (state) => state.user;
export const selectIsAuthenticated = (state) => state.isAuthenticated;
export const selectIsLoading = (state) => state.isLoading;
